import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Form, Row, Tab, Tabs } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useRouteLoaderData } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { GamePublicationState, GameRelationshipKind, GameType } from "@/const";
import { useRequest } from "@components/AppHook";
import { tmrPushEvent } from "@components/Counters";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import {
    GameNameField,
    GameSlugField,
    IframeLandingUrlsField,
    IframeVersionIncField,
    LPIframeLandingUrlsField,
    LocalizersDataField,
} from "@components/Game/Fields";
import { GameToBrowserButton, GameToGameCenterButton } from "@components/Game/Misc";
import { Awesome, DevModal, LocaleDropdown, TitleDivider } from "@components/Helpers";
import { ModalDelete } from "@components/Modals";
import { CommonCard } from "@components/Pages";
import { ScrollToElement, scroll_to_top, url_reverse } from "@components/Utils";
import Countries from "@screens/Game/Countries";
import Highlights from "@screens/Game/Highlights";

export default function Locales(props) {
    return <LocEdit {...props} />;
}

// Основные свойства
function LocEdit({ gameId, locl, header }) {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl, apiIsLoading } = useAppApi(
        url_reverse("api:game:locale:edit", { game_id: gameId, locl })
    );
    const onFormSubmit = useSubmit();
    const [loginBuyEmulation, setLoginBuyEmulation] = useState({});
    const [curLoc, setCurLoc] = useState(locl);
    const commonInfo = useRouteLoaderData("commonInfo");
    const gameCommonInfo = useRouteLoaderData("gameCommonInfo");
    const { api_get, api_post } = useRequest();
    const props = { gameId, curLoc, header, apiData, setApiData, apiIsLoading };
    const location = useLocation();
    const lastHash = useRef("");
    const { setDelData, setApiReget } = useAppContext();
    const [showAi, setShowAi] = useState(false);
    const [optionalImgFields, setOptionalImgFields] = useState(false);
    const [aiData, setAiData] = useState(null);

    const defaultTab = "metadata";
    const [activeKey, setActiveKey] = useState(defaultTab);

    const picturesField = [
        "picture",
        "logo_cover_astrum",
        "picture_horizontal",
        "picture_vertical",
        "picture_bundle",
        "video",
        "icon",
        "wallpaper",
        "art",
        "gc_waitplay",
        "desktop_icon",
    ];
    const mainform = apiData?.forms_data?.main?.data;
    const [closeMark, setCloseMark] = useState(false);
    const closeMarkRef = useRef(closeMark);

    const [historyIndex, setHistoryIndex] = useState(0);
    const [historyAi, setHistoryAi] = useState();
    const [isPermissibleLength, setIsPermissibleLength] = useState();

    const handleClose = () => setShowAi(false);

    const handlePrevAi = () => {
        const newIndex = Math.max(historyIndex - 1, 0);
        setHistoryIndex(newIndex);
        const newAiData = { ...aiData };

        newAiData.forms_data.ai.data.ai_response = historyAi[newIndex].answer;
        newAiData.forms_data.ai.data.promt = historyAi[newIndex].promt;
        // TODO: Пока костыль. Потом нужно подумать как реализовать для конкретного пользователя
        const resultRow = newAiData.forms_data.ai.data.promt.split(/\r?\n/).length + 3;
        newAiData.forms_data.ai.fields.promt.widget.attrs.rows = resultRow;

        setAiData(newAiData);
    };

    const handleNextAi = () => {
        const newAiData = { ...aiData };
        const newIndex = Math.min(historyIndex + 1, historyAi.length - 1);

        setHistoryIndex(newIndex);
        newAiData.forms_data.ai.data.ai_response = historyAi[newIndex].answer;

        newAiData.forms_data.ai.data.promt = historyAi[newIndex].promt;
        // TODO: Пока костыль. Потом нужно подумать как реализовать для конкретного пользователя
        const resultRow = newAiData.forms_data.ai.data.promt.split(/\r?\n/).length + 3;
        newAiData.forms_data.ai.fields.promt.widget.attrs.rows = resultRow;
        setAiData(newAiData);
    };

    const updateMarking = (ev) => {
        const newApiData = { ...apiData };
        const currenVal = ev.val;
        const newVal = currenVal.filter((element) => !mainform.marking?.includes(element));
        if (newVal.length > 0) {
            tmrPushEvent("marking", newVal[0]);
        }
        if (Array.isArray(currenVal)) {
            const isNotMarking = currenVal.indexOf("no_marking") === 0;
            const isNotRequired = currenVal.indexOf("no_required") === 0;

            if (isNotMarking && currenVal.length > 1) {
                mainform.marking = currenVal.filter((item) => item !== "no_marking");
            } else if (isNotRequired && currenVal.length > 1) {
                mainform.marking = currenVal.filter((item) => item !== "no_required");
            } else if (currenVal.includes("no_required")) {
                mainform.marking = ["no_required"];
            } else if (currenVal.includes("no_marking")) {
                mainform.marking = ["no_marking"];
            } else {
                mainform.marking = currenVal;
            }
            setApiData(newApiData);
        }
    };

    useEffect(() => {
        if (location.hash) {
            lastHash.current = location.hash.slice(1);
        }
        if (picturesField.includes(lastHash.current)) {
            setActiveKey("pictures");
        } else if (lastHash.current === "sys_req_web_data") {
            setActiveKey("sys_reqs");
        } else if (lastHash.current === "highlights") {
            setActiveKey("hl");
        } else if (lastHash.current === "countries") {
            setActiveKey("countries");
        } else if (lastHash.current === "social_links") {
            setActiveKey("social_links");
        }
        setTimeout(() => {
            let element = document.querySelector(`[name$="-${lastHash.current}"]`);
            if (!element) {
                element = document.querySelector(`[class$="-${lastHash.current}-anchr"]`);
            }
            if (lastHash.current && element) {
                setTimeout(() => {
                    while (element.parentElement && !element.classList.contains("form-group")) {
                        element = element.parentElement;
                    }
                    ScrollToElement(element);
                    lastHash.current = "";
                }, 100);
            }
        }, 1500);
    }, [gameCommonInfo, location, lastHash]);

    useEffect(() => {
        const newApiData = { ...apiData };
        if (mainform) {
            if (mainform.marking?.constructor.name === "Array") {
                if (mainform.marking.indexOf("no_marking") === 0 && mainform.marking.length > 1) {
                    mainform.marking = mainform.marking.filter((item) => item !== "no_marking");
                } else if (mainform.marking.includes("no_marking")) {
                    mainform.marking = ["no_marking"];
                }
            }
        }
        setApiData(newApiData);
    }, [JSON.stringify(mainform)]);

    useEffect(() => {
        closeMarkRef.current = closeMark;
    }, [closeMark]);

    useEffect(() => {
        setIsPermissibleLength(
            aiData?.forms_data?.ai?.data?.ai_response?.length <= aiData?.forms_data.ai.fields.ai_response.max_length
        );
    }, [aiData?.forms_data.ai.data.ai_response]);

    useEffect(() => {
        if (apiData?.forms_data) {
            const mf =
                gameCommonInfo.game.base_locale === curLoc
                    ? ["video", "desktop_icon"]
                          .map((f) => !apiData.forms_data.main.fields[f].widget.is_hidden)
                          .some(Boolean)
                    : false;
            const lf = ["gc_waitplay", "picture", "logo_cover_astrum", "icon", "wallpaper"]
                .map((f) => !apiData.forms_data[curLoc].fields[f].widget.is_hidden)
                .some(Boolean);
            setOptionalImgFields([mf, lf].some(Boolean));
        }
    }, [curLoc, JSON.stringify(mainform)]);

    const sendOpenMarking = () => {
        if (!closeMarkRef.current) {
            tmrPushEvent("marking", "show");
            setCloseMark(true);
        }
    };

    const sendCloseMarking = () => {
        if (closeMarkRef.current) {
            tmrPushEvent("marking", "close");
            setCloseMark(false);
        }
    };

    if (!apiData) return;

    const buyEmulationClose = () => {
        setLoginBuyEmulation({ ...loginBuyEmulation, login: null });
    };

    const buyEmulation = (ev) => {
        api_post(ev, url_reverse("api:game:misc_actions", { game_id: gameId }), {
            params: { action: ev.target.name, login: loginBuyEmulation.login },
            then: () => {
                buyEmulationClose();
                ev.target.disabled = true;
            },
        });
    };
    const sendAi = (ev) => {
        ev.preventDefault();
        const form = aiData.forms_data.ai;
        const { data } = form;
        tmrPushEvent("ai_gen_send", data.game_field, commonInfo.auth_info.user_id, gameCommonInfo.game.id);
        api_post(ev, url_reverse("api:game:ai", { game_id: gameId }), {
            params: { promt: data.promt, game_field: data.game_field, game_locale: curLoc },
            then: (resp) => {
                if (resp.status === "ok") {
                    const newAiData = { ...aiData };
                    newAiData.forms_data.ai.data.ai_response = resp.answer;
                    newAiData.forms_data.ai.data.limit = resp.limit;
                    newAiData.forms_data.ai.data.history_ai = resp.history_ai;
                    setHistoryAi(resp.history_ai);
                    setHistoryIndex(resp.history_ai.length - 1);
                    setAiData(newAiData);
                }
            },
        });
    };

    const aiFillModal = (ev) => {
        const aiField = ev.target.value;
        tmrPushEvent("ai_gen_click", aiField, commonInfo.auth_info.user_id, gameCommonInfo.game.id);
        api_get(ev, url_reverse("api:game:ai", { game_id: gameId }), {
            params: { field_name: aiField },
            then: (resp) => {
                if (resp.status === "ok") {
                    // TODO: Пока костыль. Потом нужно подумать как реализовать для конкретного пользователя
                    const resultRow = resp.forms_data.ai.data.promt.split(/\r?\n/).length + 3;
                    resp.forms_data.ai.fields.promt.widget.attrs.rows = resultRow;
                    setAiData(resp);
                    setHistoryAi(resp.history_ai);
                    setHistoryIndex(resp.history_ai.length - 1);
                    setShowAi(true);
                }
            },
        });
    };

    const draftRefresh = (ev) => {
        api_get(ev, apiUrl, {
            params: { action: "refresh_drafts" },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                    scroll_to_top();
                }
            },
        });
    };

    const showFetchParentLocaleButton = !gameCommonInfo.game.is_standalone_game && !apiData.active_locales[curLoc];
    const fetchParentLocale = (ev) => {
        api_post(ev, url_reverse("api:game:misc_actions", { game_id: gameId }), {
            params: { action: ev.target.name, locale: ev.target.value },
            then: () => {
                setApiReget(true);
            },
        });
    };

    const headerTools = (
        <>
            <LocaleDropdown
                tooltip={t("Если игра поддерживает разные языки, заполните поля локализации для каждого из языков.")}
                curLoc={curLoc}
                setCurLoc={setCurLoc}
                active_locales={apiData.active_locales}
                disabled_locales={apiData.disabled_locales}
                base_locale={gameCommonInfo.game.base_locale}
                forms_data={apiData.forms_data}
            />{" "}
            <GameToBrowserButton
                game={{ ...gameCommonInfo.game, parent: gameCommonInfo.parent }}
                asButton={true}
                withLabel={true}
            />{" "}
            <GameToGameCenterButton
                game={{ ...gameCommonInfo.game, parent: gameCommonInfo.parent }}
                asButton={true}
                withLabel={true}
            />{" "}
            {gameCommonInfo.game.is_sellable && (
                <Button
                    variant="info"
                    size="sm"
                    onClick={() =>
                        setLoginBuyEmulation({
                            ...loginBuyEmulation,
                            login: commonInfo.account.login,
                            dblConfirm: gameCommonInfo.game.type === GameType.CODE,
                        })
                    }
                >
                    <Awesome type="credit-card" /> {t("Эмуляция покупки")}
                </Button>
            )}
        </>
    );

    const fieldProps = { apiData, setApiData, apiIsLoading };
    const markerFieldData = apiData?.forms_data?.main.fields.marking.widget.attrs?.additional_information;
    const markerData = (marker) => {
        return markerFieldData.find((item) => item.slug === marker.value);
    };
    const markerImage = (option, { context }) => (
        <>
            {markerFieldData && (
                <div className="d-flex align-items-center justify-content-between">
                    <div className="mr-2">
                        <img src={markerData(option)?.icon} style={{ maxWidth: 50 }} />
                    </div>
                    <div className="flex-grow-1">
                        {context === "value" ? option.label : markerData(option).description}
                    </div>
                </div>
            )}
        </>
    );

    function errorFunction(name, locale) {
        if (picturesField.includes(name)) {
            setActiveKey("pictures");
        } else {
            setActiveKey(defaultTab);
        }
        if (locale) {
            setCurLoc(locale);
        }
    }

    const insertAnswer = (ev) => {
        const newApiData = { ...apiData };
        const fieldName = ev.target.value;
        if (fieldName === "about_text") {
            const editor = Object.keys(window.CKEDITOR.instances)[0];
            window.CKEDITOR.instances[editor].setData(aiData.forms_data.ai.data.ai_response);
        } else {
            newApiData.forms_data[curLoc].data[fieldName] = aiData.forms_data.ai.data.ai_response;
        }

        tmrPushEvent("ai_gen_use", fieldName, commonInfo.auth_info.user_id, gameCommonInfo.game.id);
        setAiData(newApiData);
        const form = aiData.forms_data.ai;
        const { data } = form;
        api_post(ev, url_reverse("api:game:ai", { game_id: gameId }), {
            params: {
                promt: data.promt,
                game_field: data.game_field,
                game_locale: curLoc,
                ai_response: aiData.forms_data.ai.data.ai_response,
            },
            then: (resp) => {
                if (resp.status === "ok") {
                    const newAiData = { ...aiData };
                    newAiData.forms_data.ai.data.ai_response = resp.answer;
                    newAiData.forms_data.ai.data.limit = resp.limit;
                    newAiData.forms_data.ai.data.history_ai = resp.history_ai;
                    setAiData(newAiData);
                }
            },
        });
        setShowAi(false);
    };

    function AiButton({ value, onClick, children, ...rest }) {
        return (
            <DevForm.Button name="ai" variant="btn btn-info btn-sm" value={value} onClick={onClick} {...rest}>
                {children}
            </DevForm.Button>
        );
    }

    const updatePromt = () => {
        const newAiData = { ...aiData };
        aiData.forms_data.ai.data.promt = aiData.base_promt;
        setAiData(newAiData);
    };

    // Удаление старых якорей при переходе между вкладками
    const handleSelect = (k) => {
        setActiveKey(k || defaultTab);
        const correctUrl = url_reverse("games:locale:edit", { game_id: gameId, locale: locl });
        window.history.pushState({}, "", correctUrl);
        location.hash = "";
        lastHash.current = "";
    };

    const pageForm = (
        <Form
            onSubmit={(ev) => {
                const urls = apiData.lpifrurls.map((o) => {
                    return o.url;
                });
                api_post(ev, url_reverse("api:game:save_lpifrurls", { game_id: gameCommonInfo.game.id }), {
                    params: { urls: encodeURIComponent(urls) },
                });
                onFormSubmit(ev);
            }}
        >
            <Tabs activeKey={activeKey} transition={false} className="react-bootstrap-tabs-fix" onSelect={handleSelect}>
                <Tab eventKey={defaultTab} title={t("Метаданные")}>
                    <DevForm.Field {...fieldProps} prefix="main" name="date_modified" />
                    <DevForm.Field {...fieldProps} prefix="main" name="game_type" />
                    <DevForm.Field {...fieldProps} prefix="real_game" name="monetization_model" />
                    <GameNameField {...fieldProps} prefix={curLoc} />
                    {gameCommonInfo.game.base_locale === curLoc && (
                        <>
                            <DevForm.Field {...fieldProps} prefix="main" name="alter_names" />
                            <DevForm.Field {...fieldProps} prefix="main" name="main_genre" />
                            <DevForm.Field {...fieldProps} prefix="main" name="genre" />
                            <DevForm.Field {...fieldProps} prefix="main" name="tags" />
                            {!gameCommonInfo.game.bundle && (
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="descr" />
                            )}
                        </>
                    )}
                    {!gameCommonInfo.game.bundle && (
                        <>
                            {!gameCommonInfo.game.demo && (
                                <DevForm.Field
                                    {...fieldProps}
                                    size="sm"
                                    prefix={curLoc}
                                    name="header_long_descr"
                                    underLabel={
                                        apiData.is_show_ai && (
                                            <AiButton value="header_long_descr" onClick={aiFillModal}>
                                                {t("Сгенерировать с помощью Ai")}
                                            </AiButton>
                                        )
                                    }
                                />
                            )}
                            <DevForm.Field
                                {...fieldProps}
                                prefix={curLoc}
                                name="long_descr"
                                underLabel={
                                    apiData.is_show_ai && (
                                        <AiButton value="long_descr" onClick={aiFillModal}>
                                            {t("Сгенерировать с помощью Ai")}
                                        </AiButton>
                                    )
                                }
                            />
                        </>
                    )}
                    <DevForm.Field
                        {...fieldProps}
                        prefix={curLoc}
                        name="about_text"
                        underLabel={
                            apiData.is_show_ai && (
                                <AiButton value="about_text" onClick={aiFillModal}>
                                    {t("Сгенерировать с помощью Ai")}
                                </AiButton>
                            )
                        }
                    />
                    {gameCommonInfo.game.base_locale === curLoc && (
                        <>
                            <div name="date_release_estimated">
                                <DevForm.Field {...fieldProps} prefix="real_game" name="date_release_estimated" />
                            </div>
                            <DevForm.Field {...fieldProps} prefix="real_game" name="date_release_estimated_variant" />
                            <DevForm.Field {...fieldProps} prefix="main" name="date_release_global" />
                            <DevForm.Field {...fieldProps} prefix="main" name="payment_callback_url" />
                            <LocalizersDataField {...fieldProps} prefix="main" name="localizers_data" />
                            <IframeLandingUrlsField {...fieldProps} />
                            <LPIframeLandingUrlsField {...fieldProps} />
                            {gameCommonInfo.game.is_browser && <IframeVersionIncField {...fieldProps} />}
                            <DevForm.Field {...fieldProps} prefix="main" name="oauth_redirect_url" />
                        </>
                    )}

                    <DevForm.Field {...fieldProps} prefix={curLoc} name="support_url" />
                    {gameCommonInfo.game.base_locale === curLoc && (
                        <>
                            <DevForm.Field {...fieldProps} prefix="main" name="age_limit" />
                            <DevForm.Field {...fieldProps} prefix="main" name="base_locale" />
                            <DevForm.Field
                                {...fieldProps}
                                prefix="main"
                                name="marking"
                                sortable={false}
                                formatOptionLabel={markerImage}
                                onChange={updateMarking}
                                onMenuClose={sendCloseMarking}
                                onMenuOpen={sendOpenMarking}
                            />
                            <GameSlugField {...fieldProps} />
                        </>
                    )}

                    {gameCommonInfo.game.base_locale === curLoc && (
                        <>
                            <DevForm.Field {...fieldProps} prefix="main" name="supported_os" />
                            {!gameCommonInfo.game.bundle && !gameCommonInfo.game.dlc && !gameCommonInfo.game.demo && (
                                <>
                                    <DevForm.Field {...fieldProps} prefix="real_game" name="m2m_gamedevs" />
                                    <DevForm.Field {...fieldProps} prefix="real_game" name="m2m_publishers" />
                                    <DevForm.Field
                                        {...fieldProps}
                                        prefix="real_game"
                                        name="show_info_about_in_development"
                                    />
                                    <DevForm.Field
                                        {...fieldProps}
                                        prefix="real_game"
                                        name="show_info_about_early_access"
                                    />
                                </>
                            )}
                            {/* Поле "Лаборатория" скрыто до востребования */}
                            {/* <DevForm.Field {...fieldProps} prefix="real_game" name="is_laboratory" /> */}
                            <DevForm.Field {...fieldProps} prefix="main" name="special_flags" />
                            <DevForm.Field {...fieldProps} prefix="main" name="only_windows_64" />
                            <DevForm.Field {...fieldProps} prefix="real_game" name="offline_mode" />
                            {Object.keys(apiData.forms_data || {}).includes("bundle") && (
                                <DevForm.Field {...fieldProps} prefix="bundle" name="bundle_type" />
                            )}
                            {Object.keys(apiData.forms_data || {}).includes("dlc") && (
                                <DevForm.Field {...fieldProps} prefix="dlc" name="dlc_type" />
                            )}
                        </>
                    )}
                </Tab>
                {!gameCommonInfo.game.demo && !(gameCommonInfo.game.dlc?.kind === GameRelationshipKind.DLC_HIDDEN) && (
                    <Tab eventKey="pictures" title={t("Изображения")}>
                        {gameCommonInfo.game.bundle && (
                            <Alert variant="warning">
                                {t(
                                    "Важно! Все обложки должны быть выполнены в одном стиле с использованием одинаковых артов"
                                )}
                            </Alert>
                        )}
                        <TitleDivider label={t("Обязательно")} />
                        <DevForm.Field {...fieldProps} prefix={curLoc} name="picture_horizontal" />
                        <DevForm.Field {...fieldProps} prefix={curLoc} name="picture_vertical" />
                        <DevForm.Field {...fieldProps} prefix={curLoc} name="art" />
                        <DevForm.Field {...fieldProps} prefix={curLoc} name="picture_bundle" />

                        {optionalImgFields && (
                            <>
                                <TitleDivider label={t("Опционально")} />
                                {gameCommonInfo.game.base_locale === curLoc && (
                                    <>
                                        <DevForm.Field {...fieldProps} prefix="main" name="video" />
                                        <DevForm.Field {...fieldProps} prefix="main" name="desktop_icon" />
                                    </>
                                )}
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="gc_waitplay" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="picture" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="logo_cover_astrum" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="icon" />
                                <DevForm.Field {...fieldProps} prefix={curLoc} name="wallpaper" />
                            </>
                        )}
                    </Tab>
                )}
                {!gameCommonInfo.game.bundle &&
                    !gameCommonInfo.game.demo &&
                    !gameCommonInfo.game.playtest &&
                    !(gameCommonInfo.game.dlc?.kind === GameRelationshipKind.DLC_HIDDEN) && (
                        <Tab eventKey="hl" title={t("Скриншоты и видео")}>
                            <Highlights {...props} />
                        </Tab>
                    )}
                {!gameCommonInfo.game.is_mini_browser &&
                    !gameCommonInfo.game.playtest &&
                    apiData.forms_data &&
                    `sys_req_min_form_${curLoc}` in apiData.forms_data && (
                        <Tab eventKey="sys_reqs" title={t("Системные требования")}>
                            <fieldset>
                                <h6>{t("Минимальные системные требования")}</h6>
                                <RenderForm prefix={`sys_req_min_form_${curLoc}`} bareForm={true} />
                            </fieldset>
                            <hr />
                            <fieldset>
                                <h6>{t("Рекомендуемые системные требования")}</h6>
                                <RenderForm prefix={`sys_req_rec_form_${curLoc}`} bareForm={true} />
                            </fieldset>
                        </Tab>
                    )}
                {apiData.is_show_social_tab && !gameCommonInfo.game.playtest && (
                    <Tab eventKey="social_links" title={t("Соцсети")}>
                        <div className="dmg-callout dmg-callout-info mb-3">
                            {t(
                                "Добавьте ссылки на сообщества проекта. После модерации они отобразятся на библиотечной странице игры блоком кликабильных иконок."
                            )}
                        </div>
                        <RenderForm prefix={`social_form_${curLoc}`} bareForm={true} />
                    </Tab>
                )}
                {!gameCommonInfo.game.bundle &&
                    !gameCommonInfo.game.dlc &&
                    !gameCommonInfo.game.demo &&
                    !gameCommonInfo.game.playtest && (
                        <Tab eventKey="countries" title={t("Страны")} style={{ marginBottom: "10px" }}>
                            <Countries {...props} />
                        </Tab>
                    )}
            </Tabs>

            <Row>
                <Col>
                    {!apiData.lock?.is_locked &&
                        (apiData.not_relevant ? (
                            <DevForm.Button variant="danger" onClick={draftRefresh} name="draft_refresh">
                                {t("Обновить")}
                            </DevForm.Button>
                        ) : (
                            !commonInfo.account.is_super &&
                            (gameCommonInfo.game.publication_state === GamePublicationState.UNPUBLISHED &&
                            gameCommonInfo.game.bundle ? (
                                <small className="text-danger">
                                    <Trans>
                                        Набор снят с публикации, редактирование недоступно. Пожалуйста создайте новый
                                        набор.
                                    </Trans>
                                </small>
                            ) : (
                                <>
                                    <DevForm.Button
                                        variant="primary"
                                        type="submit"
                                        name="action"
                                        value="save"
                                        // Костыль, чтобы при ошибке валидации видео не отправлять форму
                                        disabled={
                                            Boolean(apiData.forms_data?.main?.errors?.video) ||
                                            gameCommonInfo.game.locked_by_moderation_states
                                        }
                                    >
                                        {t("Сохранить")}
                                    </DevForm.Button>
                                    {apiData.use_drafts && (
                                        <>
                                            {" "}
                                            <DevForm.Button
                                                variant="warning"
                                                type="submit"
                                                name="action"
                                                value="moderate"
                                                disabled={
                                                    Boolean(apiData.forms_data?.main?.errors?.video) ||
                                                    gameCommonInfo.game.locked_by_moderation_states
                                                }
                                            >
                                                {t("Отправить на проверку")}
                                            </DevForm.Button>
                                        </>
                                    )}
                                </>
                            ))
                        ))}
                    {activeKey === defaultTab && curLoc !== gameCommonInfo.game.base_locale && (
                        <DevForm.Button
                            variant="danger"
                            name="delete"
                            className="float-right"
                            onClick={() =>
                                setDelData({
                                    url: url_reverse("api:game:locale:edit", {
                                        game_id: gameId,
                                        locl: curLoc,
                                    }),
                                    afterRespOK: () => {
                                        setApiReget(true);
                                    },
                                })
                            }
                        >
                            {t("Удалить локализацию")}
                        </DevForm.Button>
                    )}
                </Col>
            </Row>
        </Form>
    );

    const pageModals = (
        <>
            <ModalDelete />

            <DevModal
                size="lg"
                show={loginBuyEmulation.login && loginBuyEmulation.dblConfirm}
                onHide={buyEmulationClose}
                header={t("Эмуляция покупки")}
                body={
                    <span style={{ color: "red" }}>
                        <Trans>
                            Предупреждение: вы собираетесь проэмулировать покупку игры с типом 'Пин-код' — это серьезная
                            операция, которая требует обдуманного решения. Данная операция может быть выполнена только с
                            разрешения вашего непосредственного руководителя. Обратите внимание, что возврат средств за
                            эмуляцию невозможен, и вы несете полную ответственность за последствия. Если необходимо
                            протестировать покупку, используйте тестовые проекты, где это разрешено. Для получения
                            информации о тестовых проектах обратитесь к своему руководителю или автору задачи.
                        </Trans>
                    </span>
                }
                footer={
                    <DevForm.Button
                        variant="primary"
                        name="emulation_confirm"
                        onClick={() => setLoginBuyEmulation({ ...loginBuyEmulation, dblConfirm: false })}
                    >
                        {t("Продолжить")}
                    </DevForm.Button>
                }
            />

            <DevModal
                size="lg"
                show={loginBuyEmulation.login && !loginBuyEmulation.dblConfirm}
                onHide={buyEmulationClose}
                header={t("Эмуляция покупки")}
                body={
                    <FormFieldGroup label={t("Логин")}>
                        <DevForm.TextInput
                            value={loginBuyEmulation.login}
                            onChange={(ev) => setLoginBuyEmulation({ ...loginBuyEmulation, login: ev.target.value })}
                        />
                    </FormFieldGroup>
                }
                footer={
                    <DevForm.Button variant="primary" name="buy_emulation" onClick={buyEmulation}>
                        {t("Эмулировать")}
                    </DevForm.Button>
                }
            />

            <DevModal
                size="lg"
                show={showAi}
                onHide={handleClose}
                header={
                    <>
                        {t("AI помощник для генерации")}{" "}
                        {aiData?.forms_data?.ai.data.game_field === "about_text"
                            ? t("полного описания")
                            : aiData?.forms_data?.ai.data.game_field === "header_long_descr"
                              ? t("заголовка краткого описания")
                              : t("краткого описания")}{" "}
                    </>
                }
                body={
                    <>
                        <Trans>
                            Запрос к ИИ для генерации текста по игре на основе введённых данных. Вы можете
                            отредактировать запрос перед отправкой{" "}
                        </Trans>
                        <br />

                        <DevForm.Field prefix="ai" apiData={aiData} setApiData={setAiData} name="ai_response" />

                        <div className="form-group row">
                            <div className="col mb-2 d-flex justify-content-end">
                                {historyAi?.length > 0 && (
                                    <>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={handlePrevAi}
                                            disabled={historyIndex <= 0}
                                            className="me-2"
                                        >
                                            <i className="fa fa-angle-double-left"></i>
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={handleNextAi}
                                            disabled={historyIndex >= historyAi.length - 1}
                                            className="me-2"
                                        >
                                            <i className="fa fa-angle-double-right"></i>
                                        </Button>
                                        <span>
                                            {historyIndex + 1}/{historyAi.length}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                        <DevForm.Field prefix="ai" apiData={aiData} setApiData={setAiData} name="promt" />
                        <DevForm.Field prefix="ai" apiData={aiData} setApiData={setAiData} name="game_field" />
                        {apiData.is_show_counter && (
                            <>
                                <div className="d-flex justify-content-end mt-2">
                                    Осталось запросов: {aiData?.forms_data?.ai.data.limit}
                                </div>
                            </>
                        )}
                        <div className="d-flex justify-content-end mt-1">
                            <Button variant="primary" onClick={updatePromt}>
                                {t("Обновить данные запроса")}
                            </Button>
                        </div>
                    </>
                }
                footer={
                    <>
                        {isPermissibleLength ? (
                            <DevForm.Button
                                variant="success"
                                name="use_ai_answer"
                                value={aiData?.forms_data?.ai.data.game_field}
                                onClick={insertAnswer}
                            >
                                {t("Использовать ответ ИИ")}
                            </DevForm.Button>
                        ) : (
                            <DevForm.Button
                                disabled={true}
                                variant="success"
                                name="use_ai_answer"
                                value={aiData?.forms_data?.ai.data.game_field}
                                onClick={insertAnswer}
                                style={{ pointerEvents: "auto" }}
                                tooltip={t("Количество символов не должно превышать допустимое значение")}
                            >
                                {t("Использовать ответ ИИ")}
                            </DevForm.Button>
                        )}
                        <DevForm.Button
                            variant="primary"
                            type="submit"
                            name="ai_button"
                            disabled={apiData.is_show_counter && aiData?.forms_data?.ai.data.limit <= 0}
                        >
                            {t("Отправить запрос")}
                        </DevForm.Button>
                    </>
                }
                renderWrapper={(children) => (
                    <Form noValidate onSubmit={sendAi}>
                        {children}
                    </Form>
                )}
            />
        </>
    );

    return (
        <>
            {gameCommonInfo.game.locked_by_moderation_states && (
                <Alert variant="danger">
                    <Trans>
                        Проект находится на модерации, изменение настроек на данном этапе запрещены. Пожалуйста
                        дождитесь завершения процесса модерации.
                    </Trans>
                </Alert>
            )}
            <CommonCard header={header} headerTools={headerTools} errorFunction={errorFunction}>
                {showFetchParentLocaleButton ? (
                    !gameCommonInfo.game.locked_by_moderation_states && (
                        <DevForm.Button
                            variant="btn btn-primary"
                            name="fetch_parent_locale"
                            value={curLoc}
                            onClick={fetchParentLocale}
                        >
                            {t("Добавить локализацию")}
                        </DevForm.Button>
                    )
                ) : (
                    <>
                        {pageForm}
                        {pageModals}
                    </>
                )}
            </CommonCard>
        </>
    );
}
